import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const LineChartTracker = () => {
  const data = [
    { name: 'Birth', height: 48, weight: 3160 },
    { name: '1.5 months', height: 54, weight: 4300 },
    { name: '2.5 months', height: 58, weight: 5300 },
    { name: '3.2 months', height: 60, weight: 5730 },
    { name: '4 months', height: 62, weight: 6170 },
    { name: '4.3 months', height: 62, weight: 6550 },
    { name: '5 months', height: 65, weight: 6998 },
    { name: '6 months', height: 67, weight: 7250 },
    { name: '6.7 months', height: 68.5, weight: 7870 },
    { name: '7 months', height: 68.5, weight: 7900 },
    { name: '8 months', height: 69.5, weight: 8450 },
    { name: '10 months', height: 73, weight: 9350 },
    { name: '11 months', height: 73, weight: 9350 },
    { name: '12 months', height: 75.5, weight: 9500 },
  ];

  // Calculate statistics
  const latestData = data[data.length - 1];
  const previousData = data[data.length - 2];
  const firstData = data[0];

  const heightGrowth = latestData.height - previousData.height;
  const weightGrowth = latestData.weight - previousData.weight;
  const totalHeightGrowth = latestData.height - firstData.height;
  const totalWeightGrowth = latestData.weight - firstData.weight;

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          bg="gray.800"
          p={3}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="teal.500"
          boxShadow="0 0 20px rgba(56, 178, 172, 0.3)"
        >
          <Text fontSize="sm" fontWeight="bold" color="white" mb={2}>
            {label}
          </Text>
          {payload.map((entry, index) => (
            <HStack key={index} spacing={2}>
              <Box w={3} h={3} bg={entry.color} borderRadius="full" />
              <Text fontSize="xs" color="gray.300">
                {entry.name}: <Text as="span" fontWeight="bold">{entry.value}</Text>
                {entry.name.includes('Height') ? ' cm' : ' g'}
              </Text>
            </HStack>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <VStack spacing={6} w="full" align="stretch">
      
      {/* Header */}
      <VStack spacing={2}>
        <Heading
          size={{ base: "sm", md: "md" }}
          textAlign="center"
          color="teal.300"
          fontWeight="bold"
        >
          📈 Growth Tracker
        </Heading>
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Mattias' height and weight progress over time
        </Text>
      </VStack>

      {/* Statistics Cards */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="purple.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={3}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">Current Height</StatLabel>
              <StatNumber color="purple.300" fontSize="xl">
                {latestData.height} cm
              </StatNumber>
              <StatHelpText fontSize="xs" color="teal.300">
                <StatArrow type="increase"  />
                {heightGrowth} cm
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="green.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={3}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">Current Weight</StatLabel>
              <StatNumber color="green.300" fontSize="xl">
                {(latestData.weight / 1000).toFixed(2)} kg
              </StatNumber>
              <StatHelpText fontSize="xs" color="teal.300">
                <StatArrow type="increase" />
                {weightGrowth} g
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="teal.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={3}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">Total Growth</StatLabel>
              <StatNumber color="teal.300" fontSize="lg">
                +{totalHeightGrowth} cm
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500">
                Since birth
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>

        <Card
          bg="rgba(45, 55, 72, 0.7)"
          borderWidth="1px"
          borderColor="cyan.700"
          backdropFilter="blur(10px)"
        >
          <CardBody p={3}>
            <Stat>
              <StatLabel color="gray.400" fontSize="xs">Weight Gain</StatLabel>
              <StatNumber color="cyan.300" fontSize="lg">
                +{(totalWeightGrowth / 1000).toFixed(2)} kg
              </StatNumber>
              <StatHelpText fontSize="xs" color="gray.500">
                Since birth
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Chart Card */}
      <Card
        bg="rgba(45, 55, 72, 0.7)"
        borderWidth="2px"
        borderColor="teal.700"
        backdropFilter="blur(10px)"
        boxShadow="0 0 30px rgba(56, 178, 172, 0.2)"
      >
        <CardBody p={4}>
          <VStack spacing={4} align="stretch">
            
            {/* Chart Header */}
            <HStack justify="space-between" flexWrap="wrap">
              <Text fontSize="sm" color="gray.300" fontWeight="semibold">
                Growth Timeline
              </Text>
              <HStack spacing={3}>
                <HStack spacing={1}>
                  <Box w={3} h={3} bg="purple.400" borderRadius="full" />
                  <Text fontSize="xs" color="gray.400">Height</Text>
                </HStack>
                <HStack spacing={1}>
                  <Box w={3} h={3} bg="green.400" borderRadius="full" />
                  <Text fontSize="xs" color="gray.400">Weight</Text>
                </HStack>
              </HStack>
            </HStack>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={data}
                margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
              >
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  stroke="rgba(255, 255, 255, 0.1)" 
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11, fill: '#A0AEC0' }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 11, fill: '#A0AEC0' }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  label={{
                    value: 'Height (cm)',
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#B794F4', fontSize: 12, fontWeight: 'bold' },
                  }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: '#A0AEC0' }}
                  stroke="rgba(255, 255, 255, 0.2)"
                  tickLine={false}
                  label={{
                    value: 'Weight (g)',
                    angle: -90,
                    position: 'insideRight',
                    style: { fill: '#48BB78', fontSize: 12, fontWeight: 'bold' },
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="height"
                  stroke="#B794F4"
                  strokeWidth={3}
                  dot={{ 
                    r: 5, 
                    fill: '#B794F4',
                    strokeWidth: 2,
                    stroke: '#1A202C'
                  }}
                  activeDot={{ 
                    r: 8,
                    fill: '#B794F4',
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                  name="Height"
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="weight"
                  stroke="#48BB78"
                  strokeWidth={3}
                  dot={{ 
                    r: 5, 
                    fill: '#48BB78',
                    strokeWidth: 2,
                    stroke: '#1A202C'
                  }}
                  activeDot={{ 
                    r: 8,
                    fill: '#48BB78',
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                  name="Weight"
                />
              </LineChart>
            </ResponsiveContainer>

            {/* Chart Footer Info */}
            <HStack justify="center" pt={2}>
              <Badge colorScheme="teal" fontSize="xs" px={2} py={1}>
                📊 {data.length} measurements recorded
              </Badge>
              <Badge colorScheme="purple" fontSize="xs" px={2} py={1}>
                👶 {latestData.name}
              </Badge>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default LineChartTracker;